import {
  AlertCircle,
  Calculator,
  CheckCircle2,
  ExternalLink,
  Package2,
  Percent,
  ShoppingCart,
} from 'lucide-react';
import { memo, useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

interface CheckItemDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  items: IOneItemInPrepareForRequest[];
  onConfirm: () => void;
  onCancel: () => void;
}

// Individual issue item to improve rendering performance
const IssueItem = memo(
  ({ item, index }: { item: IOneItemInPrepareForRequest; index: number }) => {
    return (
      <div
        key={`${item.HealthyItemID}-${index}-check-list`}
        className="rounded-lg border border-muted bg-muted/20 p-3 transition-all hover:bg-muted/30"
      >
        <div className="flex flex-col space-y-2">
          <div className="flex items-start justify-between">
            <h3 className="text-sm font-semibold">{item.itemName}</h3>

            {/* Item status badges */}
            <div className="flex flex-wrap gap-1">
              {item.DiscountStatus === 'bad' && (
                <Badge
                  variant="outline"
                  className="bg-red-100 px-1.5 text-[9px] text-red-700 dark:bg-red-900/20 dark:text-red-400"
                >
                  خصم منخفض
                </Badge>
              )}
              {item.RetailStatus === 'overStock' && (
                <Badge
                  variant="outline"
                  className="bg-green-100 px-1.5 text-[9px] text-green-700 dark:bg-green-900/20 dark:text-green-400"
                >
                  زيادة مخزون تجزئة
                </Badge>
              )}
              {item.RetailStatus === 'canTransfer' && (
                <Badge
                  variant="outline"
                  className="bg-amber-100 px-1.5 text-[9px] text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
                >
                  يمكن التحويل (تجزئة)
                </Badge>
              )}
              {item.WholeSaleSalesStatus === 'overStock' && (
                <Badge
                  variant="outline"
                  className="bg-green-100 px-1.5 text-[9px] text-green-700 dark:bg-green-900/20 dark:text-green-400"
                >
                  زيادة مخزون جملة
                </Badge>
              )}
              {item.WholeSaleSalesStatus === 'canTransfer' && (
                <Badge
                  variant="outline"
                  className="bg-amber-100 px-1.5 text-[9px] text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
                >
                  يمكن التحويل (جملة)
                </Badge>
              )}
              {item.totalUnderRequests > 0 && (
                <Badge
                  variant="outline"
                  className="bg-blue-100 px-1.5 text-[9px] text-blue-700 dark:bg-blue-900/20 dark:text-blue-400"
                >
                  طلبات قيد المعالجة
                </Badge>
              )}
            </div>
          </div>

          {/* Item details - using grid for better alignment */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="flex items-center gap-1">
              <ShoppingCart className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">الكمية:</span>
              <span className="font-medium">
                {formatNumberWithCommas(item.Qty)}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <Calculator className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">السعر:</span>
              <span className="font-medium">
                {formatNumberWithCommas(item.Price)}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <Percent className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">الخصم:</span>
              <span className="font-medium">{item.DiscountEdit}%</span>
            </div>
          </div>

          {/* Issue details with improved styling */}
          <div className="rounded-md bg-background/80 p-2 text-xs backdrop-blur-sm">
            <div className="font-medium text-muted-foreground">
              تفاصيل المشكلة:
            </div>
            <ul className="mt-1 space-y-1 text-xs">
              {item.DiscountStatus === 'bad' && (
                <li className="flex items-center gap-1 text-red-600 dark:text-red-400">
                  <AlertCircle className="h-3 w-3" />
                  <span>الخصم منخفض: {item.DiscountEdit}% (المطلوب أعلى)</span>
                </li>
              )}
              {item.RetailStatus === 'overStock' && (
                <li className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <Package2 className="h-3 w-3" />
                  <span>
                    زيادة مخزون تجزئة بمقدار:{' '}
                    {formatNumberWithCommas(Math.abs(item.RetailDiff || 0))}
                  </span>
                </li>
              )}
              {item.RetailStatus === 'canTransfer' && (
                <li className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <Package2 className="h-3 w-3" />
                  <span>
                    يمكن تحويل مخزون تجزئة بمقدار:{' '}
                    {formatNumberWithCommas(Math.abs(item.RetailDiff || 0))}
                  </span>
                </li>
              )}
              {item.WholeSaleSalesStatus === 'overStock' && (
                <li className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <Package2 className="h-3 w-3" />
                  <span>
                    زيادة مخزون جملة بمقدار:{' '}
                    {formatNumberWithCommas(Math.abs(item.WholeSaleDiff || 0))}
                  </span>
                </li>
              )}
              {item.WholeSaleSalesStatus === 'canTransfer' && (
                <li className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <Package2 className="h-3 w-3" />
                  <span>
                    يمكن تحويل مخزون جملة بمقدار:{' '}
                    {formatNumberWithCommas(Math.abs(item.WholeSaleDiff || 0))}
                  </span>
                </li>
              )}
              {item.totalUnderRequests > 0 && (
                <li className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                  <Package2 className="h-3 w-3" />
                  <span>يوجد {item.totalUnderRequests} طلب قيد المعالجة</span>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  },
);

IssueItem.displayName = 'IssueItem';

export function CheckItemDialog({
  isOpen,
  onOpenChange,
  items,
  onConfirm,
  onCancel,
}: CheckItemDialogProps) {
  // Memoize filtered items to avoid recalculation on render
  const itemsWithIssues = useMemo(
    () =>
      items.filter(
        (item) =>
          item.DiscountStatus === 'bad' ||
          item.RetailStatus === 'overStock' ||
          item.RetailStatus === 'canTransfer' ||
          item.WholeSaleSalesStatus === 'overStock' ||
          item.WholeSaleSalesStatus === 'canTransfer' ||
          (item.totalUnderRequests && item.totalUnderRequests > 0),
      ),
    [items],
  );

  const hasIssues = itemsWithIssues.length > 0;
  const dialogDescriptionId = 'check-items-dialog-description';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        hiddenCloseIcon
        overlayClass="custom-dialog-overlay"
        aria-describedby={dialogDescriptionId}
        className="border-primary/10 shadow-lg dark:border-primary/20"
      >
        <DialogHeader className="space-y-1">
          <DialogTitle className="flex items-center gap-2 text-lg">
            {hasIssues ? (
              <AlertCircle className="h-5 w-5 text-amber-500" />
            ) : (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            )}
            {hasIssues
              ? 'مراجعة الأصناف قبل تأكيد الطلبية'
              : 'كل الأصناف جاهزة'}
            <DialogDescription className="text-muted-foreground">
              {hasIssues
                ? `يوجد ${itemsWithIssues.length} صنف يحتاج مراجعة قبل المتابعة`
                : 'يمكنك المتابعة بتأكيد الطلبية'}
            </DialogDescription>
          </DialogTitle>
        </DialogHeader>

        {hasIssues && (
          <ScrollArea className="mt-2 max-h-[400px] rounded-md border border-muted/30 p-2 shadow-inner">
            <div className="space-y-4">
              {itemsWithIssues.map((item, index) => (
                <IssueItem
                  key={`${item.HealthyItemID}-${index}`}
                  item={item}
                  index={index}
                />
              ))}
            </div>
          </ScrollArea>
        )}

        <DialogFooter className="gap-2 sm:gap-1">
          {!hasIssues && (
            <Button
              variant="outline"
              onClick={onCancel}
              className="w-full border-muted-foreground/20 font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground dark:border-muted-foreground/10 dark:hover:bg-muted/30 sm:w-auto"
            >
              إلغاء
            </Button>
          )}
          <Button
            onClick={onConfirm}
            className={cn(
              'w-full gap-2 bg-gradient-to-r from-green-600 to-emerald-600 font-medium text-white',
              'hover:from-green-700 hover:to-emerald-700 hover:shadow-md',
              'dark:from-green-500 dark:to-emerald-500 dark:hover:from-green-600 dark:hover:to-emerald-600',
              'shadow-sm dark:text-black sm:w-auto',
            )}
          >
            <CheckCircle2 className="h-4 w-4" />
            تأكيد الطلبية
          </Button>
          {hasIssues && (
            <Button
              variant="outline"
              className={cn(
                'w-full gap-2 border-indigo-200 bg-indigo-50/50 font-medium text-indigo-600',
                'hover:bg-indigo-100 hover:text-indigo-700',
                'dark:border-indigo-500/20 dark:bg-indigo-950/20 dark:text-indigo-400',
                'dark:hover:bg-indigo-900/30 dark:hover:text-indigo-300',
                'sm:w-auto',
              )}
              onClick={onCancel}
            >
              <ExternalLink className="h-4 w-4" />
              مراجعة تفاصيل أكثر
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
